import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  initializeChatSession,
  sendMessage,
  closeChatSession,
  ChatSession,
  SendMessageResponse
} from '../../../services/chatSupportService';
import { UserInfoFormData, MessageFormData } from '../validationSchemas';

// Constants for success and error messages
const CHAT_SUCCESS_MESSAGES = {
  SESSION_INITIALIZED: 'Chat session started successfully!',
  MESSAGE_SENT: 'Message sent successfully!',
  SESSION_CLOSED: 'Chat session closed successfully!',
} as const;

const CHAT_ERROR_MESSAGES = {
  SESSION_INIT_FAILED: 'Failed to start chat session. Please try again.',
  MESSAGE_SEND_FAILED: 'Failed to send message. Please try again.',
  SESSION_CLOSE_FAILED: 'Failed to close chat session. Please try again.',
} as const;

/**
 * Hook for initializing a new chat session
 */
export const useInitializeChatSessionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userInfo: UserInfoFormData) => initializeChatSession(userInfo),
    onSuccess: (response) => {
      if (response.success && response.data) {
        // Store session data in localStorage
        localStorage.setItem('chat_session_id', response.data.session_id);
        localStorage.setItem('chat_session_data', JSON.stringify(response.data));
        
        toast.success(CHAT_SUCCESS_MESSAGES.SESSION_INITIALIZED);
        // Invalidate chat-related queries
        queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
      } else {
        toast.error(response.error || CHAT_ERROR_MESSAGES.SESSION_INIT_FAILED);
      }
    },
    onError: (error) => {
      console.error('Initialize chat session error:', error);
      toast.error(CHAT_ERROR_MESSAGES.SESSION_INIT_FAILED);
    },
  });
};

/**
 * Hook for sending messages in a chat session
 */
export const useSendMessageMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, messageData }: { sessionId: string; messageData: MessageFormData }) =>
      sendMessage(sessionId, messageData),
    onSuccess: (response, { sessionId }) => {
      if (response.success) {
        // toast.success(CHAT_SUCCESS_MESSAGES.MESSAGE_SENT);
        // Invalidate messages for this specific session
        queryClient.invalidateQueries({ queryKey: ['chat-messages', sessionId] });
        // Also invalidate general chat queries
        queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
      } else {
        toast.error(response.error || CHAT_ERROR_MESSAGES.MESSAGE_SEND_FAILED);
      }
    },
    onError: (error) => {
      console.error('Send message error:', error);
      toast.error(CHAT_ERROR_MESSAGES.MESSAGE_SEND_FAILED);
    },
  });
};

/**
 * Hook for closing a chat session
 */
export const useCloseChatSessionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => closeChatSession(sessionId),
    onSuccess: (response, sessionId) => {
      if (response.success) {
        toast.success(CHAT_SUCCESS_MESSAGES.SESSION_CLOSED);
        // Invalidate chat-related queries
        queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
        queryClient.invalidateQueries({ queryKey: ['chat-messages', sessionId] });
      } else {
        toast.error(response.error || CHAT_ERROR_MESSAGES.SESSION_CLOSE_FAILED);
      }
    },
    onError: (error) => {
      console.error('Close chat session error:', error);
      toast.error(CHAT_ERROR_MESSAGES.SESSION_CLOSE_FAILED);
    },
  });
};
